import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

/**
 * Single async key/value API backed by the safest storage available:
 *   - Native:  iOS Keychain / Android Keystore via capacitor-secure-storage-plugin.
 *   - Web:     localStorage (no secure equivalent in webview/PWA context).
 *
 * Used for refresh tokens + biometric-unlocked session tokens. Access
 * tokens stay in-memory only (Zustand authStore) and are never written
 * here — short-lived, refreshed before expiry, lost on app kill.
 *
 * Key convention: prefix with `sec_` so a casual `localStorage` audit shows
 * what's intentionally persisted vs accidentally leaked plain auth artifacts.
 */

const isNative = (): boolean => Capacitor.isNativePlatform();

export const secureStorage = {
  async get(key: string): Promise<string | null> {
    if (isNative()) {
      try {
        const result = await SecureStoragePlugin.get({ key });
        return result.value ?? null;
      } catch (error) {
        // Plugin throws when key is missing — treat as absent rather than fatal.
        if (error instanceof Error && /not\s+found/i.test(error.message)) return null;
        console.warn(`[secureStorage] get(${key}) native failed, falling back:`, error);
      }
    }
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  async set(key: string, value: string): Promise<void> {
    if (isNative()) {
      try {
        await SecureStoragePlugin.set({ key, value });
        return;
      } catch (error) {
        console.warn(`[secureStorage] set(${key}) native failed, falling back:`, error);
      }
    }
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn(`[secureStorage] set(${key}) web failed:`, error);
    }
  },

  async remove(key: string): Promise<void> {
    if (isNative()) {
      try {
        await SecureStoragePlugin.remove({ key });
      } catch {
        /* ignore — key may simply be absent */
      }
    }
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },

  async clear(): Promise<void> {
    if (isNative()) {
      try {
        await SecureStoragePlugin.clear();
      } catch {
        /* ignore */
      }
    }
  },
};
